import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageCircle, Send, X, UserCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { UserProfile, ChatMessage } from '../../types';

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  user: UserProfile;
  messages: ChatMessage[];
  onSendMessage: (bookingId: string, text: string) => void;
  partnerName: string;
}

export const ChatModal = ({ isOpen, onClose, bookingId, user, messages, onSendMessage, partnerName }: ChatModalProps) => {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    onSendMessage(bookingId, inputText.trim());
    setInputText('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center sm:p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="relative bg-white w-full max-w-md h-[80vh] sm:h-[600px] sm:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-500">
              <UserCircle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-black text-slate-900 text-sm">{partnerName}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Dukungan Layanan</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-xl transition-colors">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Messages */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/30"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
              <MessageCircle className="w-12 h-12 mb-2 text-slate-300" />
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Belum ada pesan</p>
            </div>
          ) : (
            messages.map((m, idx) => (
              <div key={idx} className={cn(
                "flex flex-col max-w-[80%]",
                m.senderId === user.id ? "ml-auto items-end" : "mr-auto items-start"
              )}>
                <div className={cn(
                  "px-5 py-3 rounded-2xl text-xs font-medium shadow-sm leading-relaxed",
                  m.senderId === user.id ? "bg-slate-900 text-white rounded-br-none" : "bg-white text-slate-600 rounded-bl-none border border-slate-100"
                )}>
                  {m.text}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">
                    {m.senderId === m.customerId ? 'Customer' : 'Kapster'}
                  </span>
                  <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div className="p-6 bg-white border-t border-slate-100">
          <form onSubmit={handleSend} className="flex gap-2 relative">
            <input 
              type="text"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder="Tulis pesan..."
              className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-6 py-4 text-xs font-bold focus:bg-white focus:border-slate-900 outline-none transition-all pr-16"
            />
            <button 
              type="submit"
              disabled={!inputText.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-900 text-white rounded-xl flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-emerald-500 transition-all active:scale-90"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
